/**
 * Centralized Quality Score Utilities
 *
 * This module provides consistent quality score display utilities across the application.
 * All quality scores are on a 0-100 scale.
 *
 * Score Ranges V2.0 (legacy):
 * - VIVID:  80-100 (Spectacular conditions - rare)
 * - GREAT:  60-79  (Excellent conditions - worth planning for)
 * - GOOD:   40-59  (Pleasant conditions - nice colors likely)
 * - FAIR:   20-39  (Moderate conditions - some color expected)
 * - POOR:   0-19   (Limited potential - minimal colors)
 *
 * Score Ranges V2.1 (current):
 * - VIVID:  90-100 (Spectacular conditions - rare)
 * - GREAT:  70-89  (Excellent conditions - worth planning for)
 * - GOOD:   50-69  (Pleasant conditions - nice colors likely)
 * - FAIR:   35-49  (Moderate conditions - some color expected)
 * - POOR:   0-34   (Limited potential - minimal colors)
 */

export type QualityCategory = "Vivid" | "Great" | "Good" | "Fair" | "Poor";

/**
 * Quality score threshold ranges (0-100 scale)
 */
export const QUALITY_THRESHOLDS = {
  VIVID: { min: 80, max: 100 },
  GREAT: { min: 60, max: 79 },
  GOOD: { min: 40, max: 59 },
  FAIR: { min: 20, max: 39 },
  POOR: { min: 0, max: 19 },
} as const;

/**
 * Quality score threshold ranges for V2.1 algorithm (0-100 scale)
 * V2.1 adjustments:
 * - VIVID: 90-100 (raised from 80)
 * - GREAT: 70-89  (changed from 60-79)
 * - GOOD:  50-69  (changed from 40-59)
 * - FAIR:  35-49  (changed from 20-39)
 * - POOR:  0-34   (changed from 0-19)
 */
export const QUALITY_THRESHOLDS_V2_1 = {
  VIVID: { min: 90, max: 100 },
  GREAT: { min: 70, max: 89 },
  GOOD: { min: 50, max: 69 },
  FAIR: { min: 35, max: 49 },
  POOR: { min: 0, max: 34 },
} as const;

export type QualityThresholdsVersion = 'v2.0' | 'v2.1';

/**
 * Get the appropriate quality thresholds by version
 * @param version - Algorithm version ('v2.0' or 'v2.1'), defaults to 'v2.1'
 * @returns The quality thresholds for the specified version
 */
export function getQualityThresholds(version: QualityThresholdsVersion = 'v2.1') {
  return version === 'v2.1' ? QUALITY_THRESHOLDS_V2_1 : QUALITY_THRESHOLDS;
}

/**
 * Get the quality category label from a numeric score (0-100)
 * Uses V2.1 thresholds by default
 * @param score - Quality score from 0-100
 * @param version - Algorithm version (defaults to 'v2.1')
 * @returns The quality category label
 */
export function getQualityLabel(score: number, version: QualityThresholdsVersion = 'v2.1'): QualityCategory {
  const clampedScore = Math.max(0, Math.min(100, score));
  const thresholds = getQualityThresholds(version);

  if (clampedScore >= thresholds.VIVID.min) return "Vivid";
  if (clampedScore >= thresholds.GREAT.min) return "Great";
  if (clampedScore >= thresholds.GOOD.min) return "Good";
  if (clampedScore >= thresholds.FAIR.min) return "Fair";
  return "Poor";
}

/**
 * Get the quality category from a string (case-insensitive)
 * @param category - Category string (e.g., "VIVID", "vivid", "Vivid")
 * @returns Normalized QualityCategory
 */
export function normalizeQualityCategory(category: string | undefined | null): QualityCategory {
  if (!category) return "Fair";

  const normalized = category.toLowerCase();
  switch (normalized) {
    case "vivid": return "Vivid";
    case "great": return "Great";
    case "good": return "Good";
    case "fair": return "Fair";
    case "poor": return "Poor";
    default: return "Fair";
  }
}

/**
 * Get Tailwind CSS gradient classes for quality badges
 * Consistent color coding across the application:
 * - VIVID: Gold/Orange gradient (spectacular)
 * - GREAT: Orange gradient (excellent)
 * - GOOD: Yellow/Amber gradient (pleasant)
 * - FAIR: Slate/Gray (moderate)
 * - POOR: Gray/Muted (limited)
 *
 * @param categoryOrScore - Quality category string or numeric score (0-100)
 * @returns Tailwind CSS classes for the gradient background
 */
export function getQualityGradient(categoryOrScore: QualityCategory | string | number): string {
  let category: QualityCategory;

  if (typeof categoryOrScore === "number") {
    category = getQualityLabel(categoryOrScore);
  } else {
    category = normalizeQualityCategory(categoryOrScore);
  }

  switch (category) {
    case "Vivid":
      return "bg-gradient-to-r from-amber-500 to-orange-600";
    case "Great":
      return "bg-gradient-to-r from-orange-400 to-amber-500";
    case "Good":
      return "bg-gradient-to-r from-yellow-400 to-orange-400";
    case "Fair":
      return "bg-gradient-to-r from-slate-400 to-slate-500";
    case "Poor":
    default:
      return "bg-gradient-to-r from-slate-500 to-slate-600";
  }
}

/**
 * Get Tailwind CSS classes for quality badges (gradient + text color)
 * @param categoryOrScore - Quality category string or numeric score (0-100)
 * @returns Tailwind CSS classes including gradient and text color
 */
export function getQualityBadgeClasses(categoryOrScore: QualityCategory | string | number): string {
  return `${getQualityGradient(categoryOrScore)} text-white`;
}

/**
 * Get the hex color for a quality category (for non-Tailwind contexts like charts)
 * @param categoryOrScore - Quality category string or numeric score (0-100)
 * @returns Hex color string
 */
export function getQualityHexColor(categoryOrScore: QualityCategory | string | number): string {
  let category: QualityCategory;

  if (typeof categoryOrScore === "number") {
    category = getQualityLabel(categoryOrScore);
  } else {
    category = normalizeQualityCategory(categoryOrScore);
  }

  switch (category) {
    case "Vivid":
      return "#F97316"; // orange-500
    case "Great":
      return "#FB923C"; // orange-400
    case "Good":
      return "#FACC15"; // yellow-400
    case "Fair":
      return "#94A3B8"; // slate-400
    case "Poor":
    default:
      return "#64748B"; // slate-500
  }
}

/**
 * Format a quality score for display
 * @param score - Quality score (0-100)
 * @param includePercent - Whether to include the % symbol (default: true)
 * @returns Formatted score string
 */
export function formatQualityScore(score: number, includePercent: boolean = true): string {
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));
  return includePercent ? `${clampedScore}%` : `${clampedScore}`;
}

/**
 * Format quality display as "Category - Score%"
 * @param score - Quality score (0-100)
 * @param category - Optional category override (otherwise derived from score)
 * @returns Formatted string like "Vivid - 85%"
 */
export function formatQualityDisplay(score: number, category?: string): string {
  const label = category ? normalizeQualityCategory(category) : getQualityLabel(score);
  return `${label} - ${formatQualityScore(score)}`;
}

/**
 * Get description for a quality category
 * @param categoryOrScore - Quality category string or numeric score (0-100)
 * @returns Human-readable description of the quality level
 */
export function getQualityDescription(categoryOrScore: QualityCategory | string | number): string {
  let category: QualityCategory;

  if (typeof categoryOrScore === "number") {
    category = getQualityLabel(categoryOrScore);
  } else {
    category = normalizeQualityCategory(categoryOrScore);
  }

  switch (category) {
    case "Vivid":
      return "Spectacular colors expected - rare conditions with high clouds and optimal atmosphere";
    case "Great":
      return "Excellent conditions for vibrant photography - high/mid clouds with clean air";
    case "Good":
      return "Good conditions with pleasant colors - worth capturing";
    case "Fair":
      return "Moderate conditions - some color development expected";
    case "Poor":
    default:
      return "Limited color due to overcast, low clouds, or poor visibility";
  }
}

/**
 * Get the score range description for a quality category
 * Uses V2.1 thresholds by default
 * @param category - Quality category
 * @param version - Algorithm version (defaults to 'v2.1')
 * @returns Score range string like "90-100%"
 */
export function getQualityScoreRange(category: QualityCategory, version: QualityThresholdsVersion = 'v2.1'): string {
  const thresholds = getQualityThresholds(version);
  const range = thresholds[category.toUpperCase() as keyof typeof thresholds];
  return `${range.min}-${range.max}%`;
}

/**
 * Get a visual icon/symbol for a quality category
 * Provides non-color differentiation for accessibility (colorblind users)
 * @param categoryOrScore - Quality category string or numeric score (0-100)
 * @returns Unicode symbol representing the quality level
 */
export function getQualityIcon(categoryOrScore: QualityCategory | string | number): string {
  let category: QualityCategory;

  if (typeof categoryOrScore === "number") {
    category = getQualityLabel(categoryOrScore);
  } else {
    category = normalizeQualityCategory(categoryOrScore);
  }

  switch (category) {
    case "Vivid":
      return "★★★"; // 3 stars for spectacular
    case "Great":
      return "★★☆"; // 2.5 stars for excellent
    case "Good":
      return "★★"; // 2 stars for good
    case "Fair":
      return "★☆"; // 1.5 stars for fair
    case "Poor":
    default:
      return "★"; // 1 star for poor
  }
}
